import { useMemo, useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import { Calculator } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input, NativeSelect } from "@/components/ui/input";
import { emptyPlanInput, emptyPlanOutput, type ProcessPlanFormValues } from "@/features/planning/schema";
import { useCompany } from "@/features/settings/api";
import {
  calculateCrm,
  calculateParting,
  calculateSkinPass,
  millConstantsFromCompany,
  nestCtl,
  nestStrips,
  planSheetCuts,
  stripWidthFromOd,
  tubeQtyFromWeight,
  tubeWeightTons,
  type WidthGroup,
} from "@/lib/mill";

type StockBatch = {
  id: string;
  batch_no: string;
  material_id?: string;
  material_name?: string | null;
  material_grade_id?: string | null;
  quantity?: number;
  qty?: number;
  weight: number;
  thickness?: number | null;
  width?: number | null;
  length?: number | null;
  wt_per_m?: number | null;
  received_at?: string;
};

type ProcessType = { id: string; code?: string; name: string };

type Machine = {
  id: string;
  max_width_mm?: number | null;
  process_type_id: string;
};

type ShapeSize = {
  id: string;
  size_label: string;
  od?: number | null;
  thickness?: number | null;
  mother_size_id?: string | null;
  shape?: string | null;
};

/**
 * Dense calculator panel — Apply prefills stage inputs/outputs; never executes.
 */
export function MillStageAssist({
  form,
  stageIndex,
  editable,
  processTypes,
  machines,
  stockBatches,
  materials,
  shapes = [],
}: {
  form: UseFormReturn<ProcessPlanFormValues>;
  stageIndex: number;
  editable: boolean;
  processTypes: ProcessType[];
  machines: Machine[];
  stockBatches: StockBatch[];
  materials: { id: string; name: string }[];
  shapes?: ShapeSize[];
}) {
  const processTypeId = form.watch(`stages.${stageIndex}.process_type_id`);
  const machineId = form.watch(`stages.${stageIndex}.machine_id`);
  const pt = processTypes.find((p) => p.id === processTypeId);
  const code = (pt?.code || "").toUpperCase();
  const { data: company } = useCompany();
  const mill = millConstantsFromCompany(company);

  if (!editable || !code) return null;

  if (code === "HR_PARTING") {
    return (
      <PartingAssist
        form={form}
        stageIndex={stageIndex}
        stockBatches={stockBatches}
        materials={materials}
        machine={machines.find((m) => m.id === machineId)}
      />
    );
  }
  if (code === "CRM") {
    return (
      <div className="space-y-1.5">
        <CrmAssist
          form={form}
          stageIndex={stageIndex}
          stockBatches={stockBatches}
          materials={materials}
        />
        <SheetAssist
          form={form}
          stageIndex={stageIndex}
          stockBatches={stockBatches}
          materials={materials}
        />
      </div>
    );
  }
  if (code === "ANNEALING") {
    return (
      <PassThroughAssist
        form={form}
        stageIndex={stageIndex}
        stockBatches={stockBatches}
        materials={materials}
        label="Annealing"
        hint="Select which parts to anneal — siblings left out stay available for skin/slit"
      />
    );
  }
  if (code === "SKINPASS") {
    return (
      <SkinAssist
        form={form}
        stageIndex={stageIndex}
        stockBatches={stockBatches}
        materials={materials}
        factor={mill.skinpass_weight_factor}
      />
    );
  }
  if (code === "CR_SLITTING") {
    return (
      <SlitAssist
        form={form}
        stageIndex={stageIndex}
        stockBatches={stockBatches}
        materials={materials}
        shapes={shapes}
        mill={mill}
      />
    );
  }
  if (code === "TUBE") {
    return (
      <TubeAssist form={form} stageIndex={stageIndex} stockBatches={stockBatches} materials={materials} mill={mill} />
    );
  }
  if (code === "CTL") {
    return (
      <CtlAssist form={form} stageIndex={stageIndex} stockBatches={stockBatches} materials={materials} mill={mill} />
    );
  }
  return null;
}

function AssistShell({
  title,
  hint,
  children,
  onApply,
  applyDisabled,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
  onApply: () => void;
  applyDisabled?: boolean;
}) {
  return (
    <div className="space-y-1.5 rounded border border-dashed border-border bg-muted/30 p-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-2xs font-semibold uppercase tracking-wide text-muted-foreground">
          <Calculator className="size-3" />
          {title}
        </div>
        <Button type="button" size="xs" disabled={applyDisabled} onClick={onApply}>
          Apply to stage
        </Button>
      </div>
      {hint ? <p className="text-2xs text-muted-foreground">{hint}</p> : null}
      {children}
    </div>
  );
}

function PartingAssist({
  form,
  stageIndex,
  stockBatches,
  materials,
  machine,
}: {
  form: UseFormReturn<ProcessPlanFormValues>;
  stageIndex: number;
  stockBatches: StockBatch[];
  materials: { id: string; name: string }[];
  machine?: Machine;
}) {
  const [batchId, setBatchId] = useState("");
  const [globalCount, setGlobalCount] = useState(1);
  const [groups, setGroups] = useState<WidthGroup[]>([{ width: 0 }]);
  const coil = stockBatches.find((b) => b.id === batchId);

  const result = useMemo(() => {
    if (!coil?.width) return null;
    return calculateParting({
      coilWidth: coil.width,
      coilWeight: coil.weight,
      coilWtPerM: coil.wt_per_m ?? null,
      thickness: coil.thickness ?? null,
      length: coil.length ?? null,
      widthGroups: groups,
      globalCount,
      maxPartWidth: machine?.max_width_mm ?? null,
    });
  }, [coil, groups, globalCount, machine?.max_width_mm]);

  return (
    <AssistShell
      title="HR parting calculator"
      hint="Parts count per width doubles (A/B) without multiplying width budget"
      applyDisabled={!result || result.widthExceeded || !result.parts.length || !coil}
      onApply={() => {
        if (!coil || !result) return;
        const mat = coil.material_id || materials[0]?.id || "";
        form.setValue(`stages.${stageIndex}.input_source`, "inventory");
        form.setValue(`stages.${stageIndex}.inputs`, [
          {
            inventory_batch_id: coil.id,
            planned_qty: coil.qty ?? coil.quantity ?? 1,
            planned_weight: coil.weight,
          },
        ]);
        form.setValue(
          `stages.${stageIndex}.outputs`,
          result.parts.map((p) => ({
            ...emptyPlanOutput,
            material_id: mat,
            material_grade_id: coil.material_grade_id ?? "",
            planned_qty: 1,
            planned_weight: Number(p.weight.toFixed(4)),
            thickness_mm: p.thickness ?? undefined,
            width_mm: p.width,
            length_mm: p.length ?? undefined,
            notes: `Part ${p.suffix}`,
          })),
        );
      }}
    >
      <div className="grid grid-cols-12 gap-1.5">
        <div className="col-span-6">
          <NativeSelect value={batchId} onChange={(e) => setBatchId(e.target.value)}>
            <option value="">Coil batch…</option>
            {stockBatches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.batch_no}
                {b.width != null ? ` · W${b.width}` : ""} · {b.weight}
              </option>
            ))}
          </NativeSelect>
        </div>
        <div className="col-span-3">
          <Input
            type="number"
            min={1}
            value={globalCount}
            onChange={(e) => setGlobalCount(Math.max(1, Number(e.target.value) || 1))}
            title="Parts per width"
          />
        </div>
        <div className="col-span-3 flex items-center text-2xs text-muted-foreground">
          per width
        </div>
      </div>
      {groups.map((g, i) => (
        <div key={i} className="flex gap-1.5">
          <Input
            type="number"
            placeholder="Width mm"
            value={g.width || ""}
            onChange={(e) => {
              const next = [...groups];
              next[i] = { width: Number(e.target.value) || 0 };
              setGroups(next);
            }}
          />
          {groups.length > 1 ? (
            <Button
              type="button"
              size="xs"
              variant="ghost"
              onClick={() => setGroups(groups.filter((_, j) => j !== i))}
            >
              −
            </Button>
          ) : null}
        </div>
      ))}
      <Button type="button" size="xs" variant="outline" onClick={() => setGroups([...groups, { width: 0 }])}>
        + width group
      </Button>
      {result ? (
        <p
          className={`text-2xs tabular-nums ${result.widthExceeded ? "text-destructive" : "text-muted-foreground"}`}
        >
          Used {result.usedWidth.toFixed(1)} / {coil?.width ?? "?"} mm · scrap{" "}
          {result.scrapWidth.toFixed(1)} · {result.totalParts} parts · wt{" "}
          {result.totalWeight.toFixed(3)}
          {result.widthExceeded ? " · EXCEEDED" : ""}
        </p>
      ) : null}
    </AssistShell>
  );
}

function CrmAssist({
  form,
  stageIndex,
  stockBatches,
  materials,
}: {
  form: UseFormReturn<ProcessPlanFormValues>;
  stageIndex: number;
  stockBatches: StockBatch[];
  materials: { id: string; name: string }[];
}) {
  const [batchId, setBatchId] = useState("");
  const [targetThk, setTargetThk] = useState("");
  const coil = stockBatches.find((b) => b.id === batchId);
  const out = useMemo(() => {
    if (!coil?.thickness || !targetThk) return null;
    return calculateCrm({
      weight: coil.weight,
      thickness: coil.thickness,
      targetThickness: Number(targetThk),
      width: coil.width ?? null,
      wt_per_m: coil.wt_per_m ?? null,
    });
  }, [coil, targetThk]);

  return (
    <AssistShell
      title="CRM calculator"
      hint="Weight kept ≈ input; thickness → target"
      applyDisabled={!coil || !out}
      onApply={() => {
        if (!coil || !out) return;
        form.setValue(`stages.${stageIndex}.inputs`, [
          {
            inventory_batch_id: coil.id,
            planned_qty: coil.qty ?? coil.quantity ?? 1,
            planned_weight: coil.weight,
          },
        ]);
        form.setValue(`stages.${stageIndex}.outputs`, [
          {
            ...emptyPlanOutput,
            material_id: coil.material_id || materials[0]?.id || "",
            material_grade_id: coil.material_grade_id ?? "",
            planned_qty: 1,
            planned_weight: Number(out.weight.toFixed(4)),
            thickness_mm: out.thickness,
            width_mm: out.width ?? undefined,
          },
        ]);
      }}
    >
      <div className="grid grid-cols-12 gap-1.5">
        <div className="col-span-8">
          <NativeSelect value={batchId} onChange={(e) => setBatchId(e.target.value)}>
            <option value="">Input batch…</option>
            {stockBatches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.batch_no}
                {b.thickness != null ? ` · t${b.thickness}` : ""}
              </option>
            ))}
          </NativeSelect>
        </div>
        <div className="col-span-4">
          <Input
            type="number"
            step="0.01"
            placeholder="Target thk"
            value={targetThk}
            onChange={(e) => setTargetThk(e.target.value)}
          />
        </div>
      </div>
    </AssistShell>
  );
}

function PassThroughAssist({
  form,
  stageIndex,
  stockBatches,
  materials,
  label,
  hint,
}: {
  form: UseFormReturn<ProcessPlanFormValues>;
  stageIndex: number;
  stockBatches: StockBatch[];
  materials: { id: string; name: string }[];
  label: string;
  hint: string;
}) {
  const [selected, setSelected] = useState<string[]>([]);

  return (
    <AssistShell
      title={`${label} select`}
      hint={hint}
      applyDisabled={!selected.length}
      onApply={() => {
        const batches = stockBatches.filter((b) => selected.includes(b.id));
        form.setValue(
          `stages.${stageIndex}.inputs`,
          batches.map((b) => ({
            inventory_batch_id: b.id,
            planned_qty: b.qty ?? b.quantity ?? 1,
            planned_weight: b.weight,
          })),
        );
        form.setValue(
          `stages.${stageIndex}.outputs`,
          batches.map((b) => ({
            ...emptyPlanOutput,
            material_id: b.material_id || materials[0]?.id || "",
            material_grade_id: b.material_grade_id ?? "",
            planned_qty: b.qty ?? b.quantity ?? 1,
            planned_weight: b.weight,
            thickness_mm: b.thickness ?? undefined,
            width_mm: b.width ?? undefined,
            length_mm: b.length ?? undefined,
          })),
        );
      }}
    >
      <div className="max-h-28 space-y-0.5 overflow-auto text-xs">
        {stockBatches.slice(0, 40).map((b) => (
          <label key={b.id} className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={selected.includes(b.id)}
              onChange={(e) => {
                setSelected((prev) =>
                  e.target.checked ? [...prev, b.id] : prev.filter((id) => id !== b.id),
                );
              }}
            />
            <span className="tabular-nums">
              {b.batch_no} · {b.weight}
              {b.width != null ? ` · W${b.width}` : ""}
            </span>
          </label>
        ))}
      </div>
    </AssistShell>
  );
}

function SkinAssist({
  form,
  stageIndex,
  stockBatches,
  materials,
  factor,
}: {
  form: UseFormReturn<ProcessPlanFormValues>;
  stageIndex: number;
  stockBatches: StockBatch[];
  materials: { id: string; name: string }[];
  factor: number;
}) {
  const [selected, setSelected] = useState<string[]>([]);

  return (
    <AssistShell
      title="Skin pass"
      hint={`Weight × ${factor}`}
      applyDisabled={!selected.length}
      onApply={() => {
        const batches = stockBatches.filter((b) => selected.includes(b.id));
        form.setValue(
          `stages.${stageIndex}.inputs`,
          batches.map((b) => ({
            inventory_batch_id: b.id,
            planned_qty: b.qty ?? b.quantity ?? 1,
            planned_weight: b.weight,
          })),
        );
        form.setValue(
          `stages.${stageIndex}.outputs`,
          batches.map((b) => {
            const w = calculateSkinPass(b.weight, factor).weight;
            return {
              ...emptyPlanOutput,
              material_id: b.material_id || materials[0]?.id || "",
              material_grade_id: b.material_grade_id ?? "",
              planned_qty: b.qty ?? b.quantity ?? 1,
              planned_weight: Number(w.toFixed(4)),
              thickness_mm: b.thickness ?? undefined,
              width_mm: b.width ?? undefined,
              length_mm: b.length ?? undefined,
            };
          }),
        );
      }}
    >
      <div className="max-h-28 space-y-0.5 overflow-auto text-xs">
        {stockBatches.slice(0, 40).map((b) => (
          <label key={b.id} className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={selected.includes(b.id)}
              onChange={(e) => {
                setSelected((prev) =>
                  e.target.checked ? [...prev, b.id] : prev.filter((id) => id !== b.id),
                );
              }}
            />
            {b.batch_no}
          </label>
        ))}
      </div>
    </AssistShell>
  );
}

function SlitAssist({
  form,
  stageIndex,
  stockBatches,
  materials,
  shapes,
  mill,
}: {
  form: UseFormReturn<ProcessPlanFormValues>;
  stageIndex: number;
  stockBatches: StockBatch[];
  materials: { id: string; name: string }[];
  shapes: ShapeSize[];
  mill: ReturnType<typeof millConstantsFromCompany>;
}) {
  const [batchId, setBatchId] = useState("");
  const [shapeIds, setShapeIds] = useState<string[]>([]);
  const coil = stockBatches.find((b) => b.id === batchId);

  const patterns = useMemo(() => {
    if (!coil?.width) return [];
    const demands = shapeIds
      .map((id) => {
        const s = shapes.find((x) => x.id === id);
        if (!s?.od || !s.thickness) return null;
        return {
          stripWidth: stripWidthFromOd(s.od, s.thickness, mill.weld_allowance_mm),
          count: 1,
          shape_size_id: s.id,
          label: s.size_label,
        };
      })
      .filter(Boolean) as { stripWidth: number; count: number; shape_size_id: string; label: string }[];

    const fillers = shapes
      .filter((s) => s.od && s.thickness && !shapeIds.includes(s.id))
      .slice(0, 8)
      .map((s) => ({
        stripWidth: stripWidthFromOd(s.od!, s.thickness!, mill.weld_allowance_mm),
        count: 1,
        shape_size_id: s.id,
        label: s.size_label,
        isFiller: true as const,
      }));

    return nestStrips({
      coilWidth: coil.width,
      demands,
      fillers,
      constants: mill,
    });
  }, [coil, shapeIds, shapes, mill]);

  const best = patterns[0];

  return (
    <AssistShell
      title="CR slitting nest"
      hint="OD→strip · min leftover · optional fillers"
      applyDisabled={!coil || !best}
      onApply={() => {
        if (!coil || !best) return;
        form.setValue(`stages.${stageIndex}.inputs`, [
          {
            ...emptyPlanInput,
            inventory_batch_id: coil.id,
            planned_qty: 1,
            planned_weight: coil.weight,
          },
        ]);
        const stripWt =
          best.strips.reduce((n, s) => n + s.count, 0) > 0
            ? coil.weight / best.strips.reduce((n, s) => n + s.count, 0)
            : coil.weight;
        form.setValue(
          `stages.${stageIndex}.outputs`,
          best.strips.flatMap((s) =>
            Array.from({ length: s.count }, () => ({
              ...emptyPlanOutput,
              material_id: coil.material_id || materials[0]?.id || "",
              material_grade_id: coil.material_grade_id ?? "",
              shape_size_id: s.shape_size_id ?? "",
              planned_qty: 1,
              planned_weight: Number(stripWt.toFixed(4)),
              thickness_mm: coil.thickness ?? undefined,
              width_mm: Number(s.stripWidth.toFixed(2)),
              notes: s.isFiller ? `Filler ${s.label ?? ""}` : s.label ?? "",
            })),
          ),
        );
      }}
    >
      <NativeSelect value={batchId} onChange={(e) => setBatchId(e.target.value)}>
        <option value="">Mother coil…</option>
        {stockBatches.map((b) => (
          <option key={b.id} value={b.id}>
            {b.batch_no}
            {b.width != null ? ` · W${b.width}` : ""}
          </option>
        ))}
      </NativeSelect>
      <div className="max-h-24 space-y-0.5 overflow-auto text-xs">
        {shapes
          .filter((s) => s.od && s.thickness)
          .slice(0, 30)
          .map((s) => (
            <label key={s.id} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={shapeIds.includes(s.id)}
                onChange={(e) => {
                  setShapeIds((prev) =>
                    e.target.checked ? [...prev, s.id] : prev.filter((id) => id !== s.id),
                  );
                }}
              />
              {s.size_label} →{" "}
              {stripWidthFromOd(s.od!, s.thickness!, mill.weld_allowance_mm).toFixed(1)} mm
            </label>
          ))}
      </div>
      {best ? (
        <p className="text-2xs tabular-nums text-muted-foreground">
          Best: leftover {best.leftover.toFixed(1)} mm ({best.scrapPct.toFixed(1)}% scrap) · fillers{" "}
          {best.fillerCount}
        </p>
      ) : null}
    </AssistShell>
  );
}

function SheetAssist({
  form,
  stageIndex,
  stockBatches,
  materials,
}: {
  form: UseFormReturn<ProcessPlanFormValues>;
  stageIndex: number;
  stockBatches: StockBatch[];
  materials: { id: string; name: string }[];
}) {
  const [batchId, setBatchId] = useState("");
  const [sw, setSw] = useState("1250");
  const [sl, setSl] = useState("2500");
  const [qty, setQty] = useState("10");
  const coil = stockBatches.find((b) => b.id === batchId);
  const plan = useMemo(() => {
    if (!coil?.width) return null;
    const lengthEst =
      coil.wt_per_m && coil.wt_per_m > 0
        ? (coil.weight * 1000) / coil.wt_per_m
        : coil.length ?? 0;
    return planSheetCuts({
      coilWidth: coil.width,
      coilLengthEstimateMm: lengthEst,
      sheetWidth: Number(sw),
      sheetLength: Number(sl),
      qty: Number(qty) || 0,
    });
  }, [coil, sw, sl, qty]);

  return (
    <AssistShell
      title="Sheet cut planner"
      hint="FIFO coil → L×W sheets"
      applyDisabled={!coil || !plan?.fits || !plan.sheets}
      onApply={() => {
        if (!coil || !plan) return;
        form.setValue(`stages.${stageIndex}.inputs`, [
          {
            inventory_batch_id: coil.id,
            planned_qty: 1,
            planned_weight: coil.weight,
          },
        ]);
        form.setValue(`stages.${stageIndex}.outputs`, [
          {
            ...emptyPlanOutput,
            material_id: coil.material_id || materials[0]?.id || "",
            planned_qty: plan.sheets,
            planned_weight: Number(
              ((coil.weight * plan.sheets) / Math.max(plan.sheets, 1)).toFixed(4),
            ),
            width_mm: Number(sw),
            length_mm: Number(sl),
            thickness_mm: coil.thickness ?? undefined,
            notes: `Sheet · scrap~${plan.scrapAreaPct.toFixed(1)}%`,
          },
        ]);
      }}
    >
      <NativeSelect value={batchId} onChange={(e) => setBatchId(e.target.value)}>
        <option value="">Coil for sheets…</option>
        {stockBatches.map((b) => (
          <option key={b.id} value={b.id}>
            {b.batch_no}
            {b.width != null ? ` · W${b.width}` : ""}
          </option>
        ))}
      </NativeSelect>
      <div className="grid grid-cols-3 gap-1.5">
        <Input value={sw} onChange={(e) => setSw(e.target.value)} placeholder="Sheet W" />
        <Input value={sl} onChange={(e) => setSl(e.target.value)} placeholder="Sheet L" />
        <Input value={qty} onChange={(e) => setQty(e.target.value)} placeholder="Qty" />
      </div>
      {plan ? (
        <p className="text-2xs text-muted-foreground">
          {plan.fits ? "Fits" : "Short coil"} · scrap ~{plan.scrapAreaPct.toFixed(1)}%
        </p>
      ) : null}
    </AssistShell>
  );
}

function TubeAssist({
  form,
  stageIndex,
  stockBatches,
  materials,
  mill,
}: {
  form: UseFormReturn<ProcessPlanFormValues>;
  stageIndex: number;
  stockBatches: StockBatch[];
  materials: { id: string; name: string }[];
  mill: ReturnType<typeof millConstantsFromCompany>;
}) {
  const [batchId, setBatchId] = useState("");
  const [od, setOd] = useState("25.4");
  const [thk, setThk] = useState("1.2");
  const [len, setLen] = useState("6000");
  const strip = stockBatches.find((b) => b.id === batchId);

  const qty = useMemo(() => {
    if (!strip) return 0;
    return Math.floor(
      tubeQtyFromWeight(strip.weight, Number(od), Number(thk), Number(len), mill.tube_weight_factor),
    );
  }, [strip, od, thk, len, mill.tube_weight_factor]);

  return (
    <AssistShell
      title="Tube planner"
      hint="Qty from strip weight · 0.0246615 factor"
      applyDisabled={!strip || qty <= 0}
      onApply={() => {
        if (!strip) return;
        const wt = tubeWeightTons(
          Number(od),
          Number(thk),
          Number(len),
          qty,
          mill.tube_weight_factor,
        );
        form.setValue(`stages.${stageIndex}.inputs`, [
          {
            inventory_batch_id: strip.id,
            planned_qty: 1,
            planned_weight: strip.weight,
          },
        ]);
        form.setValue(`stages.${stageIndex}.outputs`, [
          {
            ...emptyPlanOutput,
            material_id: strip.material_id || materials[0]?.id || "",
            planned_qty: qty,
            planned_weight: Number(wt.toFixed(4)),
            thickness_mm: Number(thk),
            length_mm: Number(len),
            notes: `OD ${od}`,
          },
        ]);
      }}
    >
      <NativeSelect value={batchId} onChange={(e) => setBatchId(e.target.value)}>
        <option value="">Strip…</option>
        {stockBatches.map((b) => (
          <option key={b.id} value={b.id}>
            {b.batch_no} · {b.weight}
          </option>
        ))}
      </NativeSelect>
      <div className="grid grid-cols-3 gap-1.5">
        <Input value={od} onChange={(e) => setOd(e.target.value)} placeholder="OD" />
        <Input value={thk} onChange={(e) => setThk(e.target.value)} placeholder="Thk" />
        <Input value={len} onChange={(e) => setLen(e.target.value)} placeholder="Len mm" />
      </div>
      <p className="text-2xs text-muted-foreground">Suggested qty ≈ {qty} pcs</p>
    </AssistShell>
  );
}

function CtlAssist({
  form,
  stageIndex,
  stockBatches,
  materials,
  mill,
}: {
  form: UseFormReturn<ProcessPlanFormValues>;
  stageIndex: number;
  stockBatches: StockBatch[];
  materials: { id: string; name: string }[];
  mill: ReturnType<typeof millConstantsFromCompany>;
}) {
  const [batchId, setBatchId] = useState("");
  const [pieceLen, setPieceLen] = useState("6000");
  const [count, setCount] = useState("10");
  const tube = stockBatches.find((b) => b.id === batchId);

  const nest = useMemo(() => {
    if (!tube?.length) return null;
    const pieces = Array.from({ length: Math.max(1, Number(count) || 1) }, () => Number(pieceLen));
    return nestCtl({
      motherLengthMm: tube.length,
      pieceLengths: pieces,
      constants: mill,
    });
  }, [tube, pieceLen, count, mill]);

  return (
    <AssistShell
      title="CTL nest"
      hint={`Blade ${mill.ctl_blade_mm} mm + ${mill.ctl_yield_margin_pct}% yield margin`}
      applyDisabled={!tube || !nest || nest.pieces <= 0}
      onApply={() => {
        if (!tube || !nest) return;
        form.setValue(`stages.${stageIndex}.inputs`, [
          {
            inventory_batch_id: tube.id,
            planned_qty: 1,
            planned_weight: tube.weight,
          },
        ]);
        form.setValue(`stages.${stageIndex}.outputs`, [
          {
            ...emptyPlanOutput,
            material_id: tube.material_id || materials[0]?.id || "",
            planned_qty: nest.pieces,
            planned_weight: Number(
              ((tube.weight * nest.usedLength) / (tube.length || nest.usedLength)).toFixed(4),
            ),
            length_mm: Number(pieceLen),
            thickness_mm: tube.thickness ?? undefined,
            width_mm: tube.width ?? undefined,
            notes: `CTL · blade ${nest.bladesLength.toFixed(0)}mm · need~${nest.requiredWithMargin.toFixed(0)}`,
          },
        ]);
      }}
    >
      <NativeSelect value={batchId} onChange={(e) => setBatchId(e.target.value)}>
        <option value="">Tube batch…</option>
        {stockBatches.map((b) => (
          <option key={b.id} value={b.id}>
            {b.batch_no}
            {b.length != null ? ` · L${b.length}` : ""}
          </option>
        ))}
      </NativeSelect>
      <div className="grid grid-cols-2 gap-1.5">
        <Input value={pieceLen} onChange={(e) => setPieceLen(e.target.value)} placeholder="Cut len" />
        <Input value={count} onChange={(e) => setCount(e.target.value)} placeholder="Qty" />
      </div>
      {nest ? (
        <p className="text-2xs text-muted-foreground">
          Fits {nest.pieces} · leftover {nest.leftover.toFixed(0)} mm · demand+margin{" "}
          {nest.requiredWithMargin.toFixed(0)}
        </p>
      ) : null}
    </AssistShell>
  );
}
